// ./src/utils/api/UpvoteCountService.ts
//
// Guest upvote counts for an event's items — the by-target tally the host/crew see as a badge
// on the signup-item / POI / accommodation lists. Guests cast upvotes on the public event
// website (the unified `event_guest_upvote` table); the management app only READS the counts,
// so this is a single authed query, not a CRUD service. See em-scaffold-ui.
import { gql } from "@apollo/client";
import { OpResult } from "@sdflc/api-helpers";

import { ApiService } from "./ApiService";

const UPVOTE_COUNTS_QUERY = gql`
  query EventGuestUpvoteCounts($eventId: ID!) {
    eventGuestUpvoteCounts(eventId: $eventId) {
      code
      data {
        targetKind
        targetId
        count
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

export interface UpvoteCount {
  targetKind: string;
  targetId: string;
  count: number;
}

export class UpvoteCountService {
  /** All upvote counts for an event, per (targetKind, targetId). */
  static async counts(eventId: string): Promise<OpResult> {
    return ApiService.executeQuery(UPVOTE_COUNTS_QUERY, { eventId }, { fetchPolicy: "network-only" });
  }

  /**
   * Convenience: fetch counts and return a { "kind:id" → count } map the list pages merge into
   * their rows. Fails soft (an empty map) so a counts hiccup never blocks the item list.
   */
  static async countMap(eventId: string): Promise<Record<string, number>> {
    try {
      const res = await UpvoteCountService.counts(eventId);
      const rows = (res?.data as UpvoteCount[] | undefined) ?? [];
      const map: Record<string, number> = {};
      for (const r of rows) {
        map[`${r.targetKind}:${r.targetId}`] = Number(r.count) || 0;
      }
      return map;
    } catch {
      return {};
    }
  }
}
